import { skipToken } from '@reduxjs/toolkit/dist/query';
import useServerSideQuery, {
  ServerSideQueryParams,
} from 'hooks/useServerSideQuery';
import { useMemo } from 'react';
import { useGetAllocationsClientV2Query } from 'store/slices/clientV2/allocations';
import { useGetKitRequestLinesQuery } from 'store/slices/clientV2/kitRequests';
import { useGetHandlingRequestsV2Query } from 'store/slices/clientV2/handlingRequests';
import { useGetShipmentLinesQuery } from 'store/slices/clientV2/shipments';
import {
  ALL,
  DISABLE_PAGE_LIMIT,
  Lite,
  NullOperatorValues,
  QueryFilterOperators,
} from 'types/api';
import { AllocationSchema } from 'types/clientV2/allocations';
import {
  ShipmentLineSchema,
  ShipmentLineSchemaLite,
  ShipmentSchemaLite,
} from 'types/clientV2/shipments';
import {
  KitRequestLineSchema,
  KitRequestSchema,
  KitRequestSchemaLite,
} from 'types/kitting';
import {
  HandlingRequestSchema,
  HandlingRequestSchemaLite,
} from 'types/clientV2/stock';
import {
  calculateKitRequestStatus,
  combineDecoratedKitRequestLinesWithStatuses,
  createDecoratedKitRequestLines,
  KitRequestAllocationStatus,
  KitRequestLineStats,
} from 'hooks/useGetDecoratedKitRequestLines/utils';
import _ from 'lodash';
import { useGetStockLotsServerSideQuery } from 'store/slices/apiV1/inventory';
import {
  StockLocationLite,
  StockLotQuerySchema,
  StockLotQuerySchemaLite,
} from 'types/inventory';
import { typedGroupBy } from 'utils/typedLodash';

export type KitRequestDataLite = Lite<
  KitRequestSchema,
  'shipTo' | 'sourceLocations' | 'kittedFor' | 'team' | 'org'
>;
export type HandlingRequestDataLite = Pick<
  HandlingRequestSchemaLite,
  | 'id'
  | 'kitRequestLine'
  | 'requestType'
  | 'stockLot'
  | 'kitRequestIdViaLot'
  | 'completedAt'
>;

export type KitRequestLineDataLineLite = Omit<
  Lite<
    KitRequestLineSchema,
    'orgPart' | 'customProperties' | 'orgParts' | 'v2OrgParts'
  >,
  'kitRequest'
> & {
  kitRequest: Pick<KitRequestSchemaLite, 'id' | 'approved' | 'approvedBy'>;
};

export type StockLotDataLite = StockLotQuerySchemaLite;

export type KitRequestLineDataAllocationLite = Lite<
  AllocationSchema,
  'stockLot'
>;

export type ShipmentLineDataLite = Pick<
  ShipmentLineSchemaLite,
  | 'id'
  | 'shipment'
  | 'packedAt'
  | 'packedBy'
  | 'ingestedAt'
  | 'stockLot'
  | 'kitRequestLine'
  | 'shippedQuant'
  | 'status'
  | 'internalNotes'
  | 'externalNotes'
  | 'printedNotes'
> & {
  shipment: Pick<
    ShipmentSchemaLite,
    'id' | 'shipmentCode' | 'shippedAt' | 'trackingNumber'
  >;
};

export type DecoratedKitRequestLine = Omit<
  KitRequestLineDataLineLite,
  'allocations'
> & {
  allocations: (KitRequestLineDataAllocationLite & {
    calculatedStatus: KitRequestAllocationStatus;
  })[];
  shipmentLines: (Omit<ShipmentLineDataLite, 'stockLot'> & {
    stockLot: StockLotDataLite;
  })[];
  handlingRequests: HandlingRequestDataLite[];
  stats: KitRequestLineStats;
  stockLots: StockLotDataLite[];
  allocatedQuantity: number;
};

type UseGetDecoratedKitRequestLinesProps = {
  selectedKitRequestIds: KitRequestSchema['id'][];
  facility?: StockLocationLite['id'];
};

const getKitRequestLineQueryParams = (
  selectedKitRequestIds: KitRequestSchema['id'][]
): ServerSideQueryParams<KitRequestLineSchema, KitRequestLineDataLineLite> => ({
  schema: [
    ALL,
    {
      orgPart: [],
      kitRequest: ['id', 'approvedBy', 'approved'],
      customProperties: [],
      orgParts: [],
      v2OrgParts: [],
    },
  ],
  filters: [
    {
      field: 'kitRequest.id',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
  ],
  pageSize: DISABLE_PAGE_LIMIT,
});

const getAllocationQueryParams = (
  selectedKitRequestIds: KitRequestSchema['id'][]
): ServerSideQueryParams<
  AllocationSchema,
  KitRequestLineDataAllocationLite
> => ({
  schema: [
    'id',
    'quant',
    'kitRequestLine',
    {
      stockLot: [],
    },
  ],
  filters: [
    {
      field: 'kitRequestLine.kitRequest',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
  ],
  pageSize: DISABLE_PAGE_LIMIT,
});

const getShipmentLineQueryParams = (
  selectedKitRequestIds: KitRequestSchema['id'][]
): ServerSideQueryParams<ShipmentLineSchema, ShipmentLineDataLite> => ({
  schema: [
    'id',
    'shipment',
    'packedAt',
    'packedBy',
    'ingestedAt',
    'kitRequestLine',
    'stockLot',
    'shippedQuant',
    'status',
    'externalNotes',
    'internalNotes',
    'printedNotes',
    {
      shipment: ['id', 'shipmentCode', 'shippedAt', 'trackingNumber'],
    },
  ],
  filters: [
    {
      field: 'kitRequestLine.kitRequest',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
  ],
  pageSize: DISABLE_PAGE_LIMIT,
});

const getHandlingRequestQueryParams = (
  selectedKitRequestIds: KitRequestSchema['id'][]
): ServerSideQueryParams<HandlingRequestSchema, HandlingRequestDataLite> => ({
  schema: [
    'id',
    'kitRequestLine',
    'stockLot',
    'requestType',
    'kitRequestIdViaLot',
    'completedAt',
  ],
  filters: [
    {
      field: 'kitRequestIdViaLot',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
    {
      field: 'completedAt',
      operator: QueryFilterOperators.isEmpty,
      value: NullOperatorValues.isEmpty,
    },
    {
      // eslint-disable-next-line no-restricted-syntax
      field: 'cancelledAt',
      operator: QueryFilterOperators.isEmpty,
      value: NullOperatorValues.isEmpty,
    },
  ],
  pageSize: DISABLE_PAGE_LIMIT,
});

const getStockLotQueryParams = (
  selectedKitRequestIds: KitRequestSchema['id'][]
): ServerSideQueryParams<StockLotQuerySchema, StockLotDataLite> => ({
  schema: [ALL, { part: [] }],
  filters: [
    {
      // @ts-ignore we have to do this because we are filtering multiple levels deep and that is not supported by the current typing
      field: 'allocations.kitRequestLine.kitRequest',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
    'or',
    {
      // @ts-ignore we have to do this because we are filtering multiple levels deep and that is not supported by the current typing
      field: 'shipmentLines.kitRequestLine.kitRequest',
      operator: QueryFilterOperators.isAnyOf,
      value: selectedKitRequestIds,
    },
  ],
  pageSize: DISABLE_PAGE_LIMIT,
});

export const useGetDecoratedKitRequestLines = ({
  selectedKitRequestIds,
  facility,
}: UseGetDecoratedKitRequestLinesProps) => {
  const {
    data: kitRequestLinesData,
    isFetching: isFetchingKitRequestLines,
    isLoading: isLoadingKitRequestLines,
    refetch: refetchKitRequestLines,
  } = useServerSideQuery<KitRequestLineSchema, KitRequestLineDataLineLite>(
    useGetKitRequestLinesQuery,
    selectedKitRequestIds.length > 0
      ? getKitRequestLineQueryParams(selectedKitRequestIds)
      : skipToken
  );

  const {
    data: allocationsData,
    isFetching: isFetchingAllocations,
    isLoading: isLoadingAllocations,
    refetch: refetchAllocations,
  } = useServerSideQuery<AllocationSchema, KitRequestLineDataAllocationLite>(
    useGetAllocationsClientV2Query,
    selectedKitRequestIds.length > 0
      ? getAllocationQueryParams(selectedKitRequestIds)
      : skipToken
  );

  const {
    data: shipmentLinesData,
    isFetching: isFetchingShipmentLines,
    isLoading: isLoadingShipmentLines,
    refetch: refetchShipmentLines,
  } = useServerSideQuery<ShipmentLineSchema, ShipmentLineDataLite>(
    useGetShipmentLinesQuery,
    selectedKitRequestIds.length > 0
      ? getShipmentLineQueryParams(selectedKitRequestIds)
      : skipToken
  );

  const {
    data: handlingRequestsData,
    isFetching: isFetchingHandlingRequests,
    isLoading: isLoadingHandlingRequests,
    refetch: refetchHandlingRequests,
  } = useServerSideQuery<HandlingRequestSchema, HandlingRequestDataLite>(
    useGetHandlingRequestsV2Query,
    selectedKitRequestIds.length > 0
      ? getHandlingRequestQueryParams(selectedKitRequestIds)
      : skipToken
  );

  const {
    data: stockLotsData,
    isFetching: isFetchingStockLots,
    isLoading: isLoadingStockLots,
    refetch: refetchStockLots,
  } = useServerSideQuery<StockLotQuerySchema, StockLotQuerySchemaLite>(
    useGetStockLotsServerSideQuery,
    selectedKitRequestIds.length > 0
      ? getStockLotQueryParams(selectedKitRequestIds)
      : skipToken
  );

  const kitRequestLines =
    selectedKitRequestIds.length > 0 ? kitRequestLinesData?.data : undefined;

  const allocations =
    selectedKitRequestIds.length > 0 ? allocationsData?.data : undefined;

  const shipmentLines =
    selectedKitRequestIds.length > 0 ? shipmentLinesData?.data : undefined;

  const handlingRequests =
    selectedKitRequestIds.length > 0
      ? _.uniqBy(handlingRequestsData?.data, 'id')
      : undefined;

  const stockLots =
    selectedKitRequestIds.length > 0 ? stockLotsData?.data : undefined;

  const decoratedKitRequestLines = useMemo(
    () =>
      createDecoratedKitRequestLines({
        kitRequestLines,
        allocations,
        shipmentLines,
        handlingRequests,
        stockLots,
      }),
    [kitRequestLines, allocations, shipmentLines, handlingRequests, stockLots]
  );

  const kitRequestStatuses = useMemo(() => {
    if (!decoratedKitRequestLines) return undefined;
    const grouped = typedGroupBy(
      decoratedKitRequestLines,
      (line) => line.kitRequest.id
    );
    return _.mapValues(grouped, (lines) =>
      calculateKitRequestStatus(lines, facility)
    );
  }, [decoratedKitRequestLines, facility]);

  const decoratedKitRequestLinesWithStatuses = useMemo(
    () =>
      combineDecoratedKitRequestLinesWithStatuses({
        decoratedKitRequestLines,
        kitRequestStatuses: kitRequestStatuses ?? {},
      }),
    [decoratedKitRequestLines, kitRequestStatuses]
  );

  const isLoadingDecoratedKitRequestLines =
    isLoadingKitRequestLines ||
    isLoadingAllocations ||
    isLoadingShipmentLines ||
    isLoadingHandlingRequests ||
    isLoadingStockLots;

  const isFetchingDecoratedKitRequestLines =
    isFetchingKitRequestLines ||
    isFetchingAllocations ||
    isFetchingShipmentLines ||
    isFetchingHandlingRequests ||
    isFetchingStockLots;

  const refetchDecoratedKitRequestLines = () => {
    if (selectedKitRequestIds.length) {
      refetchKitRequestLines();
      refetchAllocations();
      refetchShipmentLines();
      refetchHandlingRequests();
      refetchStockLots();
    }
  };

  return {
    kitRequestLines,
    kitRequestStatuses,
    allocations,
    stockLots,
    shipmentLines,
    decoratedKitRequestLines: decoratedKitRequestLinesWithStatuses,
    isLoadingKitRequestLines,
    isLoadingAllocations,
    isLoadingShipmentLines,
    isLoadingHandlingRequests,
    isLoadingStockLots,
    isLoadingDecoratedKitRequestLines,
    isFetchingDecoratedKitRequestLines,
    refetchDecoratedKitRequestLines,
  };
};
